// src/pages/markets/api/useAlerts.ts

import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { AlertItem } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AlertsResponse {
    event_id: string;
    alerts: AlertItem[];
    alert_count: number;
}

export interface EventAlertsResult {
    alerts: AlertItem[];
    loading: boolean;
    error: Error | null;
    /** The request for this event has finished, with an answer or an error. */
    settled: boolean;
}

// ---------------------------------------------------------------------------
// useEventAlerts
//
// Fetches all alerts for a given event_id.
// No pagination — backend returns all alerts newest-first.
// ---------------------------------------------------------------------------

export function useEventAlerts(
    eventId: string | null | undefined,
): EventAlertsResult {
    const [alerts, setAlerts] = useState<AlertItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // Compared with the event asked for: the first render with a new event
    // comes before its request has started.
    const [settledFor, setSettledFor] = useState<string | null>(null);

    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!eventId) {
            setAlerts([]);
            setLoading(false);
            setError(null);
            return;
        }

        abortRef.current?.abort();
        // A new request is not settled by an earlier one's answer.
        setSettledFor(null);
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);

        apiGet<AlertsResponse>(`/api/alerts/event/${encodeURIComponent(eventId)}`, ctrl.signal)
            .then((data) => {
                if (ctrl.signal.aborted) return;
                setAlerts(data.alerts ?? []);
                setLoading(false);
                setSettledFor(eventId);
            })
            .catch((err: Error) => {
                if (ctrl.signal.aborted || err?.name === 'AbortError') return;
                console.error('[useEventAlerts] fetch failed:', err);
                setError(err);
                setLoading(false);
                setSettledFor(eventId);
            });

        return () => { ctrl.abort(); };
    }, [eventId]);

    return {
        alerts,
        loading,
        error,
        settled: !!eventId && settledFor === eventId,
    };
}